import api from '../../../../Services/api';
import Globals from '../../../../Globals.json';
import {
  userCPF,
  userDataByCEP,
  userDataByCNPJ,
} from '../../../../Services/utilitiesService';
import {
  AddressItem,
  ContactItem,
  toAddressPayload,
  toContactPayload,
} from './personForm';
import { onlyDigits } from './masks';
import { parseApiError } from './apiErrors';

const PEOPLE = Globals.api.people;

export async function findByDocument(document: string): Promise<any[]> {
  const digits = onlyDigits(document);
  if (!digits) return [];
  const { data } = await api.get(
    `${PEOPLE}?filter[cpf_cnpj]=${digits}&paginate=false`,
  );
  const list: any[] = Array.isArray(data?.data) ? data.data : [];
  // O filtro da API é parcial; só interessa o documento idêntico.
  return list.filter((person) => onlyDigits(person?.cpf_cnpj) === digits);
}

export async function getPerson(uuid: string) {
  const { data } = await api.get(`${PEOPLE}/${uuid}`);
  return data?.data;
}

export async function createPerson(payload: Record<string, any>) {
  const { data } = await api.post(PEOPLE, payload);
  return data?.data;
}

export async function updatePerson(uuid: string, payload: Record<string, any>) {
  const { data } = await api.put(`${PEOPLE}/${uuid}`, payload);
  return data?.data;
}

async function createAddress(uuid: string, address: AddressItem) {
  const payload = toAddressPayload(address);
  const { data } = await api.post(`${PEOPLE}/${uuid}/addresses`, payload);
  const created = data?.data;
  // O POST de endereço ignora cidade/UF; o PUT grava. Completa logo em seguida.
  if (created?.id && (!created.city || !created.uf) && (payload.city || payload.uf)) {
    await api.put(`${PEOPLE}/${uuid}/addresses/${created.id}`, {
      ...payload,
      id: created.id,
    });
  }
}

const sameAddress = (a: AddressItem, b: AddressItem) =>
  JSON.stringify(toAddressPayload(a)) === JSON.stringify(toAddressPayload(b));

const sameContact = (a: ContactItem, b: ContactItem) =>
  JSON.stringify(toContactPayload(a)) === JSON.stringify(toContactPayload(b));

/** Diferença entre a lista salva e a lista editada na tela. */
export function diffItems<T extends { key: string }>(
  original: T[],
  current: T[],
  isSame: (a: T, b: T) => boolean,
) {
  const currentKeys = new Set(current.map((item) => item.key));
  const originalByKey = new Map(original.map((item) => [item.key, item]));

  return {
    removed: original.filter((item) => !currentKeys.has(item.key)),
    created: current.filter((item) => !originalByKey.has(item.key)),
    updated: current.filter((item) => {
      const before = originalByKey.get(item.key);
      return before !== undefined && !isSame(before, item);
    }),
  };
}

/**
 * Na edição, o PUT de /people ignora endereços e contatos: cada mudança
 * precisa ir para o endpoint próprio. Retorna a descrição do que falhou.
 */
export async function syncRelations(
  uuid: string,
  original: { addresses: AddressItem[]; contacts: ContactItem[] },
  current: { addresses: AddressItem[]; contacts: ContactItem[] },
) {
  const failures: string[] = [];

  const run = async (label: string, action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      failures.push(`${label}: ${parseApiError(error).message}`);
    }
  };

  const addresses = diffItems(original.addresses, current.addresses, sameAddress);
  const contacts = diffItems(original.contacts, current.contacts, sameContact);
  const addressLabel = (a: AddressItem) =>
    `Endereço "${a.address_street}, ${a.address_number}"`;
  const contactLabel = (c: ContactItem) => `Contato "${c.name_1}"`;

  for (const item of addresses.removed) {
    await run(addressLabel(item), () =>
      api.delete(`${PEOPLE}/${uuid}/addresses/${item.id}`),
    );
  }
  for (const item of addresses.updated) {
    await run(addressLabel(item), () =>
      api.put(`${PEOPLE}/${uuid}/addresses/${item.id}`, toAddressPayload(item)),
    );
  }
  for (const item of addresses.created) {
    await run(addressLabel(item), () => createAddress(uuid, item));
  }

  for (const item of contacts.removed) {
    await run(contactLabel(item), () =>
      api.delete(`${PEOPLE}/${uuid}/contacts/${item.uuid}`),
    );
  }
  for (const item of contacts.updated) {
    await run(contactLabel(item), () =>
      api.put(`${PEOPLE}/${uuid}/contacts/${item.uuid}`, toContactPayload(item)),
    );
  }
  for (const item of contacts.created) {
    await run(contactLabel(item), () =>
      api.post(`${PEOPLE}/${uuid}/contacts`, toContactPayload(item)),
    );
  }

  // A API às vezes responde "removido" sem remover; confere antes de dizer que deu certo.
  if (addresses.removed.length > 0 || contacts.removed.length > 0) {
    const fresh = await getPerson(uuid).catch(() => null);
    const stillThere = (list: any[] | undefined, match: (row: any) => boolean) =>
      (list ?? []).some(match);
    const notRemoved = 'o servidor não concluiu a exclusão.';

    addresses.removed.forEach((item) => {
      const label = `${addressLabel(item)}: ${notRemoved}`;
      if (
        !failures.some((f) => f.startsWith(addressLabel(item))) &&
        stillThere(fresh?.relationships?.addresses, (row) => row.id === item.id)
      ) {
        failures.push(label);
      }
    });
    contacts.removed.forEach((item) => {
      const label = `${contactLabel(item)}: ${notRemoved}`;
      if (
        !failures.some((f) => f.startsWith(contactLabel(item))) &&
        stillThere(fresh?.relationships?.contacts, (row) => row.uuid === item.uuid)
      ) {
        failures.push(label);
      }
    });
  }

  return failures;
}

/* ---- Consultas externas (preenchimento automático) ---- */

const toIsoDate = (value?: string) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value ?? '');
  return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
};

export async function lookupCpf(cpf: string) {
  const { data } = await userCPF(onlyDigits(cpf));
  if (data?.status !== true || !data?.result) return null;
  return {
    name: String(data.result.nome ?? ''),
    birthdate: toIsoDate(data.result.data_de_nascimento),
  };
}

export async function lookupCnpj(cnpj: string) {
  const { data } = await userDataByCNPJ(onlyDigits(cnpj));
  if (data?.status !== true || !data?.result) return null;
  const r = data.result;
  return {
    name: String(r.nome ?? ''),
    trading_name: String(r.fantasia ?? ''),
    email: String(r.email ?? '').toLowerCase(),
    state_registration: String(r.numero_de_inscricao ?? ''),
    phone: onlyDigits(r.telefone).slice(0, 11),
    address: r.logradouro
      ? {
          address_zip_code: String(r.cep ?? ''),
          address_street: String(r.logradouro ?? ''),
          address_number: String(r.numero ?? ''),
          address_complement: String(r.complemento ?? ''),
          address_district: String(r.bairro ?? ''),
          city: String(r.municipio ?? ''),
          uf: String(r.uf ?? ''),
        }
      : null,
  };
}

export async function lookupCep(cep: string) {
  const { data } = await userDataByCEP(onlyDigits(cep));
  if (data?.status !== true || !data?.result) return null;
  const r = data.result;
  return {
    address_street: String(r.logradouro ?? ''),
    address_complement: String(r.complemento ?? ''),
    address_district: String(r.bairro ?? ''),
    city: String(r.localidade ?? ''),
    uf: String(r.uf ?? '').toUpperCase(),
  };
}
